import React from 'react';
import {
  EOISectionObserverReviseSuggestionPanel,
  EOISectionObserverSuggestionViewPanel,
  EOISectionObserverUpdatePanel,
} from './EOISectionAdvisorPanels';
import {
  EOISectionInitialPanel,
  EOISectionReferrerPanel,
  EOISectionSubmittedPanel,
  EOISectionUpdatePanel,
  EOISectionWithdrawPanel,
} from './EOISectionInvestorPanels';
import {
  EOISectionDecisionMakerSuggestedPanel,
  EOISectionInvestorSuggestedPanel,
} from './EOISectionSuggestedPanels';
import type { EOISectionProps } from './eoiTypes';
import type { EOISectionState } from './useEOISectionState';

type ContentProps = EOISectionProps & {
  state: EOISectionState;
  printMode: boolean;
};

export const EOISectionContent: React.FC<ContentProps> = ({ state, printMode, ...props }) => {
  const {
    investor,
    advisingFor,
    advisorMode = false,
    advisorRole = null,
    isCustodian = false,
    acceptingInvitation = false,
    referrer,
  } = props;

  const {
    noSubmission,
    hasSuggestedEOI,
    isObserver,
    isCurrentAdvisorSuggestion,
    withdrawMode,
    updateMode,
  } = state;

  const sharedPanelProps = { ...state, advisingFor, isCustodian, acceptingInvitation, referrer };

  if (referrer && !printMode && !hasSuggestedEOI) {
    return <EOISectionReferrerPanel {...sharedPanelProps} />;
  }

  if (!referrer && noSubmission && !hasSuggestedEOI) {
    return <EOISectionInitialPanel {...sharedPanelProps} />;
  }

  if (!noSubmission && !isObserver && withdrawMode) {
    return (
      <EOISectionWithdrawPanel
        advisingFor={advisingFor}
        isCustodian={isCustodian}
        withdrawing={state.withdrawing}
        setWithdrawMode={state.setWithdrawMode}
        handleWithdraw={state.handleWithdraw}
      />
    );
  }

  if (!noSubmission && !isObserver && updateMode) {
    return (
      <EOISectionUpdatePanel
        advisingFor={advisingFor}
        isCustodian={isCustodian}
        investmentInput={state.investmentInput}
        validationError={state.validationError}
        confirmed={state.confirmed}
        validInvestment={state.validInvestment}
        investmentMessage={state.investmentMessage}
        submitting={state.submitting}
        setInvestmentInput={state.setInvestmentInput}
        setUpdateMode={state.setUpdateMode}
        handleSubmit={state.handleSubmit}
      />
    );
  }

  if (!noSubmission && !withdrawMode && !updateMode && !hasSuggestedEOI) {
    return (
      <EOISectionSubmittedPanel
        advisingFor={advisingFor}
        isCustodian={isCustodian}
        isObserver={isObserver}
        investmentInput={state.investmentInput}
        validationError={state.validationError}
        confirmed={state.confirmed}
        investmentMessage={state.investmentMessage}
        suggesting={state.suggesting}
        setConfirmed={state.setConfirmed}
        setUpdateMode={state.setUpdateMode}
        setWithdrawMode={state.setWithdrawMode}
      />
    );
  }

  if (!noSubmission && !withdrawMode && updateMode && isObserver && !hasSuggestedEOI) {
    return (
      <EOISectionObserverUpdatePanel
        advisingFor={advisingFor}
        investmentInput={state.investmentInput}
        validationError={state.validationError}
        validInvestment={state.validInvestment}
        investmentMessage={state.investmentMessage}
        suggestionComment={state.suggestionComment}
        suggesting={state.suggesting}
        submitting={state.submitting}
        setInvestmentInput={state.setInvestmentInput}
        setSuggestionComment={state.setSuggestionComment}
        resetSuggestionEdit={state.resetSuggestionEdit}
        handleSubmit={state.handleSubmit}
      />
    );
  }

  if (
    !noSubmission &&
    !withdrawMode &&
    updateMode &&
    isObserver &&
    hasSuggestedEOI &&
    isCurrentAdvisorSuggestion
  ) {
    return (
      <EOISectionObserverReviseSuggestionPanel
        investor={investor}
        advisingFor={advisingFor}
        investmentInput={state.investmentInput}
        validationError={state.validationError}
        validInvestment={state.validInvestment}
        investmentMessage={state.investmentMessage}
        suggestionComment={state.suggestionComment}
        suggesting={state.suggesting}
        submitting={state.submitting}
        setInvestmentInput={state.setInvestmentInput}
        setSuggestionComment={state.setSuggestionComment}
        resetSuggestionEdit={state.resetSuggestionEdit}
        handleSubmit={state.handleSubmit}
      />
    );
  }

  if (hasSuggestedEOI && !isObserver && !advisorMode) {
    return (
      <EOISectionInvestorSuggestedPanel
        investor={investor}
        confirmed={state.confirmed}
        accepting={state.accepting}
        rejecting={state.rejecting}
        setConfirmed={state.setConfirmed}
        handleAcceptSuggestedEOI={state.handleAcceptSuggestedEOI}
        handleRejectSuggestedEOI={state.handleRejectSuggestedEOI}
      />
    );
  }

  if (hasSuggestedEOI && advisorMode && advisorRole === 'DECISION_MAKER') {
    return (
      <EOISectionDecisionMakerSuggestedPanel
        investor={investor}
        confirmed={state.confirmed}
        accepting={state.accepting}
        rejecting={state.rejecting}
        setConfirmed={state.setConfirmed}
        handleAcceptSuggestedEOI={state.handleAcceptSuggestedEOI}
        handleRejectSuggestedEOI={state.handleRejectSuggestedEOI}
      />
    );
  }

  if (hasSuggestedEOI && isObserver && !updateMode) {
    return (
      <EOISectionObserverSuggestionViewPanel
        investor={investor}
        isCurrentAdvisorSuggestion={isCurrentAdvisorSuggestion}
        suggesting={state.suggesting}
        withdrawing={state.withdrawing}
        setUpdateMode={state.setUpdateMode}
        handleWithdrawSuggestedEOI={state.handleWithdrawSuggestedEOI}
      />
    );
  }

  return null;
};
